import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useState } from 'react';
import { Box } from '@radix-ui/themes';
import { IntentionsPicker } from '@/components/intentions';
import { WizardScreen } from '@/components/layout';
import { INTENTION_IDS, type IntentionId } from '@/constants/intentions';
import { useOnboardingStore } from '@/stores/onboardingStore';
import type { OnboardingStepProps } from './types';

function isIntentionId(value: string): value is IntentionId {
  return (INTENTION_IDS as readonly string[]).includes(value);
}

/** Matches WizardHeader row height (IconButton size 2 + pb="2"). WizardScreen adds pt="5". */
const HEADER_SPACER = <Box aria-hidden height="40px" />;

export function SetIntentionsStep({ flow }: OnboardingStepProps) {
  const { t } = useTranslation(['onboarding', 'profile']);
  const loading = useOnboardingStore(state => state.loading);
  const storeIntentions = useOnboardingStore(state => state.intentions);
  const setIntentions = useOnboardingStore(state => state.setIntentions);
  const setTakingABreak = useOnboardingStore(state => state.setTakingABreak);
  const setError = useOnboardingStore(state => state.setError);
  const completeOnboarding = useOnboardingStore(state => state.completeOnboarding);

  const [selected, setSelected] = useState<IntentionId[]>(() =>
    storeIntentions.filter(isIntentionId)
  );
  const [offThisWeek, setOffThisWeek] = useState(false);

  const submit = async () => {
    if (!offThisWeek && selected.length === 0) {
      const message = t('profile:errors.intentionsRequired');
      setError(message);
      toast.error(message);
      return;
    }

    setError(null);
    setIntentions(offThisWeek ? [] : selected);
    setTakingABreak(offThisWeek);
    try {
      await completeOnboarding();
      flow.finish();
    } catch {
      // Store sets error state; stay on the final step.
    }
  };

  return (
    <WizardScreen header={HEADER_SPACER}>
      <IntentionsPicker
        selected={selected}
        offThisWeek={offThisWeek}
        onSelectedChange={setSelected}
        onOffThisWeekChange={setOffThisWeek}
        onSubmit={() => void submit()}
        loading={loading}
      />
    </WizardScreen>
  );
}
